/**
 * ตัวเรียก API
 *
 * สิ่งที่ตัวนี้รับผิดชอบมี 3 อย่างเท่านั้น
 *   1. แนบ access token ให้ทุกคำขอ
 *   2. ต่ออายุ token อัตโนมัติเมื่อหมดอายุ แล้วยิงคำขอเดิมซ้ำ — ครั้งเดียว
 *   3. แปลง error ของ API ให้เป็น ApiError ที่มีข้อความภาษาไทยพร้อมแสดงบนจอ
 *
 * ★ ข้อความ error มาจาก API เสมอ ไม่แต่งเพิ่มที่นี่ ★
 * API เขียนข้อความมาให้คนหน้าร้านอ่านรู้เรื่องอยู่แล้ว เช่น
 * "เกี๊ยวซ่าชีส คืนได้อีกแค่ 8 ชิ้น แต่ขอคืน 9" — ถ้าหน้าจอเขียนทับด้วย
 * "เกิดข้อผิดพลาด" พนักงานจะไม่รู้ว่าต้องแก้อะไร ซึ่งคือปัญหาของระบบเดิม
 */

const BASE = '/api';
const STORE_KEY = 'sushimai.session';

export interface Session {
  accessToken: string;
  refreshToken: string;
  user: {
    id: number;
    username: string;
    displayName: string;
    role: 'staff' | 'owner';
    branchId: number | null;
  };
}

export class ApiError extends Error {
  status: number;
  code: string;
  detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/** ต่อ API ไม่ได้เลย — คนละเรื่องกับ API ตอบว่าทำรายการไม่ได้ */
export class OfflineError extends Error {
  constructor() {
    super('ต่อกับเซิร์ฟเวอร์ไม่ได้ ตรวจสัญญาณเน็ตแล้วลองใหม่');
    this.name = 'OfflineError';
  }
}

/* ── ที่เก็บ session ────────────────────────────────────────── */

let current: Session | null = readStored();
const listeners = new Set<(s: Session | null) => void>();

function readStored(): Session | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  return current;
}

export function setSession(session: Session | null): void {
  current = session;
  try {
    if (session) localStorage.setItem(STORE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORE_KEY);
  } catch {
    /* โหมดส่วนตัวเขียนไม่ได้ — ยังใช้งานต่อได้ในหน่วยความจำ */
  }
  for (const fn of listeners) fn(session);
}

export function onSessionChange(fn: (s: Session | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ── การเรียก ───────────────────────────────────────────────── */

async function raw(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;

  try {
    return await fetch(BASE + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: 'no-store',
    });
  } catch {
    throw new OfflineError();
  }
}

async function toResult<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* ไม่ใช่ JSON — จัดการด้านล่าง */
  }

  if (res.ok) return parsed as T;

  const err = (parsed as { error?: { code?: string; message?: string; detail?: unknown } })?.error;
  throw new ApiError(
    res.status,
    err?.code ?? 'UNKNOWN',
    err?.message ?? `เซิร์ฟเวอร์ตอบรหัส ${res.status}`,
    err?.detail,
  );
}

/** ต่ออายุพร้อมกันหลายคำขอต้องยิงจริงครั้งเดียว ไม่งั้น refresh token ชนกันเอง */
let refreshing: Promise<Session | null> | null = null;

async function refreshSession(): Promise<Session | null> {
  const session = current;
  if (!session) return null;

  refreshing ??= (async () => {
    try {
      const res = await raw('POST', '/auth/refresh', { refreshToken: session.refreshToken });
      if (!res.ok) {
        setSession(null);
        return null;
      }
      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      const next: Session = { ...session, ...data };
      setSession(next);
      return next;
    } catch {
      // ต่อเน็ตไม่ได้ ไม่ใช่ token เสีย — อย่าเตะพนักงานออกจากระบบกลางบิล
      return null;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res = await raw(method, path, body, current?.accessToken);

  if (res.status === 401 && current) {
    const next = await refreshSession();
    if (next) res = await raw(method, path, body, next.accessToken);
  }

  return toResult<T>(res);
}

const qs = (params: Record<string, string | number | boolean | undefined>): string => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export const api = {
  get: <T>(path: string, params: Record<string, string | number | boolean | undefined> = {}) =>
    request<T>('GET', path + qs(params)),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/* ── เข้าสู่ระบบ ─────────────────────────────────────────────── */

export async function login(username: string, password: string): Promise<Session> {
  const res = await raw('POST', '/auth/login', { username, password });
  const data = await toResult<Session>(res);
  setSession(data);
  return data;
}

export function logout(): void {
  setSession(null);
}

/**
 * รหัสประจำการกระทำหนึ่งครั้ง — ส่งไปกับคำขอที่ทำซ้ำไม่ได้
 *
 * ★ กันบิลซ้ำเวลาเน็ตอืด ★ พนักงานกดปุ่มสองครั้งเพราะครั้งแรกไม่มีอะไรเกิดขึ้น
 * ถ้าทั้งสองครั้งใช้รหัสเดียวกัน เซิร์ฟเวอร์จะคืนบิลใบเดิมแทนที่จะสร้างใบที่สอง
 *
 * randomUUID มีเฉพาะใน secure context — ตอน dev ที่เปิดผ่าน IP ในวงแลนจะไม่มี
 * จึงต้องมีทางสำรองที่ใช้ getRandomValues ซึ่งมีอยู่ทุกที่
 */
export function newOpId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // เวอร์ชัน 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // ตัวแปรมาตรฐาน
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
